import type { User, Usuario } from "./types";

async function handle(res: Response) {
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Erro ${res.status}`);
  return data;
}

/**
 * Usuário logado nesta sessão. Enviado no header das operações de usuários para
 * o servidor validar que apenas administradores criam/editam/excluem.
 *
 * Inicializado direto do localStorage (não só via useEffect no App): componentes
 * que já nascem montados no primeiro render pós-login/refresh (ex.: o painel do
 * Admin Cybersecurity) disparam suas próprias requisições ANTES do useEffect do
 * App rodar (efeitos de filhos rodam antes do efeito do pai) — sem isso, a
 * primeira leitura ia sempre com o header vazio e tomava 403.
 */
function readUsuarioInicial(): string {
  try {
    const raw = localStorage.getItem("sercamp-portal-user");
    return raw ? (JSON.parse(raw) as { usuario?: string }).usuario ?? "" : "";
  } catch {
    return "";
  }
}
let sessionUser = readUsuarioInicial();
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });

/** Lista os usuários do Portal (sem a senha). Só administradores. */
export async function listUsuarios(): Promise<Usuario[]> {
  return fetch("/api/usuarios", { headers: authHeaders() }).then(handle) as Promise<Usuario[]>;
}

/**
 * Resposta do salvamento: além do usuário, o que aconteceu com o LOGIN ÚNICO —
 * em quais sistemas o acesso foi criado/desativado (ver server/credenciais.ts).
 */
type RespostaUsuario = Usuario & { propagacao?: string };

/** Cria um usuário com cargo. Só administradores. */
export async function createUsuario(data: Partial<Usuario> & { senha: string }) {
  return fetch("/api/usuarios", {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  }).then(handle) as Promise<RespostaUsuario>;
}

/** Atualiza um usuário (senha em branco mantém a atual). Só administradores. */
export async function updateUsuario(id: number, data: Partial<Usuario> & { senha?: string }) {
  return fetch(`/api/usuarios/${id}`, {
    method: "PUT",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  }).then(handle) as Promise<RespostaUsuario>;
}

/** Exclui um usuário. Só administradores. */
export async function removeUsuario(id: number) {
  return fetch(`/api/usuarios/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(handle);
}

export interface EventoSeguranca {
  id: number;
  sistema: string;
  usuario: string | null;
  acao: string;
  recurso_tipo: string | null;
  recurso_id: string | null;
  detalhes: unknown;
  ip: string | null;
  criado_em: string;
}

export interface ResumoSeguranca {
  cards: {
    total_eventos: number;
    logins_24h: number;
    falhas_login_24h: number;
    usuarios_ativos_24h: number;
  };
  porSistema: { sistema: string; total: number; ultimo_evento_em: string }[];
  porAcao: { acao: string; total: number }[];
  falhasRecentes: { sistema: string; usuario: string | null; ip: string | null; criado_em: string }[];
}

/** Painel exclusivo do Admin Cybersecurity — somente leitura. */
export async function listarEventosSeguranca(filtros: {
  sistema?: string;
  acao?: string;
  usuario?: string;
  de?: string;
  ate?: string;
  pagina?: number;
  porPagina?: number;
}): Promise<{ eventos: EventoSeguranca[]; total: number; pagina: number; porPagina: number }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== "") params.set(k, String(v));
  }
  return fetch(`/api/security/eventos?${params.toString()}`, { headers: authHeaders() }).then(
    handle,
  ) as Promise<{ eventos: EventoSeguranca[]; total: number; pagina: number; porPagina: number }>;
}

/** Cartões/resumo agregado do painel do Admin Cybersecurity — somente leitura. */
export async function resumoSeguranca(): Promise<ResumoSeguranca> {
  return fetch("/api/security/resumo", { headers: authHeaders() }).then(handle) as Promise<ResumoSeguranca>;
}

export interface LinhaDoTempoSeguranca {
  porHora: { hora: string; logins: number; falhas: number; total: number }[];
  porDia: { dia: string; falhas: number; total: number }[];
}

/** Volume de eventos ao longo do tempo (para os gráficos). Só Admin Cybersecurity. */
export async function linhaDoTempoSeguranca(): Promise<LinhaDoTempoSeguranca> {
  return fetch("/api/security/linha-do-tempo", { headers: authHeaders() }).then(
    handle,
  ) as Promise<LinhaDoTempoSeguranca>;
}

export interface AnaliseUsuariosEIps {
  topUsuarios: {
    usuario: string;
    total: number;
    ips_distintos: number;
    sistemas_distintos: number;
    ultimo_evento_em: string;
  }[];
  topIps: { ip: string; total: number; usuarios_distintos: number; ultimo_evento_em: string }[];
}

/** Ranking de usuários/IPs mais ativos. Só Admin Cybersecurity. */
export async function analiseUsuariosEIps(): Promise<AnaliseUsuariosEIps> {
  return fetch("/api/security/usuarios", { headers: authHeaders() }).then(
    handle,
  ) as Promise<AnaliseUsuariosEIps>;
}

export interface AlertasSeguranca {
  forcaBruta: { sistema: string; usuario: string; falhas: number; ultima_falha: string }[];
  horarioIncomum: {
    id: number;
    sistema: string;
    usuario: string | null;
    ip: string | null;
    criado_em: string;
  }[];
  exclusoesEmMassa: { sistema: string; usuario: string; hora: string; exclusoes: number }[];
  ipNovo: { usuario: string; ip: string; primeira_vez: string }[];
}

/** Sinalização automática de comportamento suspeito. Só Admin Cybersecurity. */
export async function alertasSeguranca(): Promise<AlertasSeguranca> {
  return fetch("/api/security/alertas", { headers: authHeaders() }).then(
    handle,
  ) as Promise<AlertasSeguranca>;
}

export interface NotificacaoSeguranca {
  id: number;
  tipo: "falha" | "manutencao" | "comunicado";
  titulo: string;
  mensagem: string;
  sistema_alvo: string | null;
  criado_por: string;
  ativo: boolean;
  expira_em: string | null;
  criado_em: string;
}

/**
 * Cria um aviso/notificação (falha, manutenção ou comunicado) para um sistema
 * específico (ou todos, se sistemaAlvo for omitido). Única capacidade de
 * ESCRITA do Admin Cybersecurity em toda a suíte.
 */
export async function criarNotificacaoSeguranca(data: {
  tipo: "falha" | "manutencao" | "comunicado";
  titulo: string;
  mensagem: string;
  sistemaAlvo?: string | null;
  expiraEm?: string | null;
}): Promise<NotificacaoSeguranca> {
  return fetch("/api/security/notificacoes", {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  }).then(handle) as Promise<NotificacaoSeguranca>;
}

/** Lista todos os avisos (ativos e desativados). Só Admin Cybersecurity. */
export async function listarNotificacoesSeguranca(): Promise<NotificacaoSeguranca[]> {
  return fetch("/api/security/notificacoes", { headers: authHeaders() }).then(
    handle,
  ) as Promise<NotificacaoSeguranca[]>;
}

/** Desativa um aviso (soft — não apaga o histórico). Só Admin Cybersecurity. */
export async function desativarNotificacaoSeguranca(id: number): Promise<NotificacaoSeguranca> {
  return fetch(`/api/security/notificacoes/${id}/desativar`, {
    method: "PATCH",
    headers: authHeaders(),
  }).then(handle) as Promise<NotificacaoSeguranca>;
}

// ---- Diagnóstico & Testes de Segurança (somente leitura, sob demanda) ----

export interface SaudeSistema {
  sistema: string;
  nome: string;
  porta: number;
  online: boolean;
  status: number | null;
  tempoMs: number;
  erro?: string;
}
export async function diagnosticoSaude(): Promise<{ resultados: SaudeSistema[] }> {
  return fetch("/api/security/diagnostico/saude", { headers: authHeaders() }).then(handle) as Promise<{
    resultados: SaudeSistema[];
  }>;
}

export interface CabecalhosSistema {
  sistema: string;
  nome: string;
  online: boolean;
  presentes: string[];
  faltando: string[];
}
export async function diagnosticoCabecalhos(): Promise<{ resultados: CabecalhosSistema[] }> {
  return fetch("/api/security/diagnostico/cabecalhos", { headers: authHeaders() }).then(handle) as Promise<{
    resultados: CabecalhosSistema[];
  }>;
}

export interface ExposicaoSistema {
  sistema: string;
  nome: string;
  status: number | null;
  exposto: boolean;
}
export async function diagnosticoExposicao(): Promise<{ resultados: ExposicaoSistema[] }> {
  return fetch("/api/security/diagnostico/exposicao", { headers: authHeaders() }).then(handle) as Promise<{
    resultados: ExposicaoSistema[];
  }>;
}

export interface SegredoSistema {
  sistema: string;
  nome: string;
  status: "ausente" | "padrao" | "customizado";
}
export async function diagnosticoSegredos(): Promise<{ resultados: SegredoSistema[] }> {
  return fetch("/api/security/diagnostico/segredos", { headers: authHeaders() }).then(handle) as Promise<{
    resultados: SegredoSistema[];
  }>;
}

export interface IsolamentoSistema {
  sistema: string;
  nome: string;
  isolado: boolean;
}
export async function diagnosticoIsolamento(): Promise<{ resultados: IsolamentoSistema[] }> {
  return fetch("/api/security/diagnostico/isolamento", { headers: authHeaders() }).then(handle) as Promise<{
    resultados: IsolamentoSistema[];
  }>;
}

/** Autentica no backend (PostgreSQL). Lança Error com mensagem amigável se falhar. */
export async function login(usuario: string, senha: string): Promise<User> {
  let res: Response;
  try {
    res = await fetch("/api/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ usuario, senha }),
    });
  } catch {
    throw new Error("Sem conexão com o servidor. Verifique se o Portal está no ar.");
  }

  const data = (await res.json().catch(() => ({}))) as {
    user?: User;
    error?: string;
  };

  if (!res.ok || !data.user) {
    throw new Error(data.error ?? "Não foi possível entrar.");
  }
  return data.user;
}
